import CheckOutlined from '@mui/icons-material/CheckOutlined'
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined'
import FilterAltOutlined from '@mui/icons-material/FilterAltOutlined'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { handleException } from 'clients/exception-handling'
import {
  IconButton,
  TableCaptionActions,
  TableCaptionRow,
  TableCaptionTitle,
} from 'features/ask-yogi/features/conversation/components/message-content/message-content-styles'
import {
  parseMarkdownTable,
  tableToHtml,
  tableToTsv,
} from 'features/ask-yogi/features/conversation/components/message-content/table-clipboard'
// bully ignore[no-cross-feature-barrel-imports: trackers/ has an index.ts so the rule treats 'trackers/mixpanel' as a barrel path, but it's a direct-module import — the mixpanel singleton lives at trackers/mixpanel.ts and every Ask Yogi consumer (Chart, MessageActions, Citation, Container) uses this exact shape and is grandfathered. No non-barrel path to the mixpanel singleton exists. permanent: rule miscalibration — would need to narrow no-cross-feature-barrel-imports to distinguish direct-module paths from barrel re-exports.]
import mixpanel from 'trackers/mixpanel'

const CHECK_FLIP_MS = 2000

export interface TableToolbarProps {
  /** Raw markdown of just this table (no `<table>` wrapper). */
  tableMarkdown: string
  /** Optional table title rendered on the left of the caption row. */
  title?: string
  /** When present, a filter icon is rendered to the LEFT of the copy button. */
  onFilterClick?: () => void
  /** Ask Yogi Anywhere variant — smaller icons. */
  compact?: boolean
  messageUuid?: string
  conversationUuid?: string
  /** Index of this table within the message (0-based) — mixpanel property. */
  tableIndex: number
}

const TableToolbar: React.FC<TableToolbarProps> = ({
  tableMarkdown,
  title,
  onFilterClick,
  compact = false,
  messageUuid,
  conversationUuid,
  tableIndex,
}) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async (): Promise<void> => {
    const rows = parseMarkdownTable(tableMarkdown)
    const tsv = tableToTsv(rows)
    const html = tableToHtml(rows)

    try {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([tsv], { type: 'text/plain' }),
      })
      await navigator.clipboard.write([clipboardItem])
      setCopied(true)
      setTimeout(() => setCopied(false), CHECK_FLIP_MS)
    } catch (cause) {
      handleException(cause, { operation: 'conversation.table.copy' })

      toast.error('Failed to copy table')
    }

    mixpanel.track('askyogi', {
      action: 'table_copied',
      table_index: tableIndex,
      row_count: rows.rows.length,
      col_count: rows.headers.length,
      message_uuid: messageUuid,
      conversation_uuid: conversationUuid,
    })
  }

  const iconSize = compact ? 12 : 14

  return (
    <TableCaptionRow>
      <TableCaptionTitle>{title && <span>{title}</span>}</TableCaptionTitle>
      <TableCaptionActions>
        {onFilterClick && (
          <Tooltip content="Filters">
            <IconButton
              type="button"
              aria-label="Filters"
              onClick={onFilterClick}
            >
              <FilterAltOutlined style={{ fontSize: iconSize }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip content={copied ? 'Copied' : 'Copy table'}>
          <IconButton
            type="button"
            aria-label="Copy table"
            onClick={() => {
              void handleCopy()
            }}
          >
            {copied ? (
              <CheckOutlined style={{ fontSize: iconSize }} />
            ) : (
              <ContentCopyOutlined style={{ fontSize: iconSize }} />
            )}
          </IconButton>
        </Tooltip>
      </TableCaptionActions>
    </TableCaptionRow>
  )
}

export default TableToolbar
